import { useEffect, useRef, useState as useStateRaw, type ReactNode } from 'react';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import { useLanguageContext } from '@/i18n/LanguageContext';
import Seo from '@/components/seo/Seo';

const ISHII_URL = 'https://getstage.com/';

function FadeInSection({ children, className = '' }: { children: ReactNode; className?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useStateRaw(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(el);
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={ref} className={`fade-in-section ${isVisible ? 'is-visible' : ''} ${className}`}>
      {children}
    </div>
  );
}

function renderParagraphs(text: string, className?: string) {
  return text.split(/(?:\r?\n){2,}/).map((part, i) => (
    <p key={i} className={className}>
      {part}
    </p>
  ));
}

export default function RelatedIshiiYuichiPage() {
  const { t } = useTranslation();
  const { lang } = useLanguageContext();
  const href = (path: string) => (lang === 'en' ? (path === '/' ? '/en' : `/en${path}`) : path);

  const points = [1, 2, 3, 4];

  const gallery = [
    { src: '/images/related/ishii-yuichi/harvard-lecture-01.jpg', alt: t('related.ishii.gallery.harvard') },
    { src: '/images/related/ishii-yuichi/fr-llc-01.jpg', alt: t('related.ishii.gallery.frLlc01') },
    { src: '/images/related/ishii-yuichi/fr-llc-02.jpg', alt: t('related.ishii.gallery.frLlc02') },
  ];

  const breadcrumbStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: t('related.ishii.hero.home'), item: 'https://butler-academy.jp/' },
      { '@type': 'ListItem', position: 2, name: t('related.ishii.hero.parent'), item: 'https://butler-academy.jp/related' },
      { '@type': 'ListItem', position: 3, name: t('related.ishii.hero.current'), item: 'https://butler-academy.jp/related/ishii-yuichi' },
    ],
  };

  const webPageStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: t('related.ishii.hero.title'),
    description: t('related.ishii.hero.lead').replace(/\r?\n/g, ''),
    url: 'https://butler-academy.jp/related/ishii-yuichi',
    isPartOf: {
      '@type': 'WebSite',
      name: lang === 'en' ? 'Japan Butler Academy' : '日本執事アカデミー',
      url: 'https://butler-academy.jp/',
    },
    about: {
      '@type': 'Person',
      name: lang === 'en' ? 'Yuichi Ishii' : '石井裕一',
      url: ISHII_URL,
      jobTitle: lang === 'en' ? 'Third Todori (head) of The All Japan Butler Association / Representative Director, The All Japan Butler Association (an incorporated association)' : '全日本執事協会 三代目頭取（一般社団法人全日本執事協会 代表理事）',
    },
  };

  return (
    <>
      <Header />
      <main>
        <Seo route="/related/ishii-yuichi" jsonLd={[breadcrumbStructuredData, webPageStructuredData]} />

        {/* Hero */}
        <section className="hero-billboard relative w-full h-[60vh] min-h-[460px] md:h-[82vh] md:min-h-[640px] overflow-hidden">
          <img
            src="/images/related/ishii-yuichi/profile-trenchcoat.jpg"
            alt={t('related.ishii.hero.imgAlt')}
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 hero-overlay"></div>
          <div className="absolute inset-0 flex items-center">
            <div className="relative w-full px-8">
              <div className="max-w-[800px] mx-auto text-center">
                <div className="flex items-center justify-center gap-2 mb-8 text-[14px] tracking-[0.05em]">
                  <Link to={href('/')} className="text-foreground-200/70 hover:text-accent-400 transition-colors">
                    {t('related.ishii.hero.home')}
                  </Link>
                  <i className="ri-arrow-right-s-line text-foreground-400/60 text-[12px]"></i>
                  <Link to={href('/related')} className="text-foreground-200/70 hover:text-accent-400 transition-colors">
                    {t('related.ishii.hero.parent')}
                  </Link>
                  <i className="ri-arrow-right-s-line text-foreground-400/60 text-[12px]"></i>
                  <span className="text-accent-400">{t('related.ishii.hero.current')}</span>
                </div>
                <h1 className="font-heading text-2xl md:text-4xl text-foreground-50 tracking-[0.08em] mb-4">
                  {t('related.ishii.hero.title')}
                </h1>
                {String(t('related.ishii.hero.titleEn')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="section-title-en text-accent-400 text-[15px] md:text-[17px] tracking-[0.12em] mb-6">{__s}</p>))}
                {renderParagraphs(
                  t('related.ishii.hero.lead'),
                  'text-[15px] md:text-[16px] text-foreground-200/80 tracking-[0.05em] leading-relaxed max-w-[620px] mx-auto'
                )}
              </div>
            </div>
          </div>
        </section>
      <Breadcrumb items={[{ label: '関連事業', labelEn: 'Related Businesses', to: '/related' }, { label: '石井裕一', labelEn: 'Yuichi Ishii' }]} />

        {/* About */}
        <FadeInSection>
          <section className="relative py-20 md:py-28 bg-background-50">
            <div className="max-w-[860px] mx-auto px-8">
              <div className="text-center mb-10">
                {String(t('related.ishii.about.label')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="text-accent-600 text-[13px] tracking-[0.2em] mb-5 font-label">{__s}</p>))}
                <h2 className="font-heading text-xl md:text-2xl text-foreground-900 tracking-[0.06em] mb-2">
                  {t('related.ishii.about.title')}
                </h2>
                {String(t('related.ishii.about.subtitleEn')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="section-title-en text-accent-500 text-[15px] tracking-[0.12em] mb-4">{__s}</p>))}
                <div className="gold-line mx-auto"></div>
              </div>
              <div className="space-y-6 text-[15px] md:text-[16px] text-foreground-600 tracking-[0.03em] leading-loose">
                <p>
                  {t('related.ishii.about.para1')}{' '}
                  <a
                    href={ISHII_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-accent-600 hover:text-accent-500 underline transition-colors"
                  >
                    {t('related.ishii.about.para1Link')}
                  </a>
                </p>
                <p>
                  {t('related.ishii.about.para2')}{' '}
                  <a
                    href={ISHII_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-accent-600 hover:text-accent-500 underline transition-colors"
                  >
                    {t('related.ishii.about.para2Link')}
                  </a>
                </p>
                {renderParagraphs(t('related.ishii.about.para3'))}
              </div>
            </div>
          </section>
        </FadeInSection>

        {/* Highlights */}
        <FadeInSection>
          <section className="relative py-20 md:py-28 bg-background-100/50">
            <div className="max-w-[1100px] mx-auto px-8">
              <div className="text-center mb-14">
                {String(t('related.ishii.points.label')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="text-accent-600 text-[13px] tracking-[0.2em] mb-5 font-label">{__s}</p>))}
                <h2 className="font-heading text-xl md:text-2xl text-foreground-900 tracking-[0.06em] mb-2">
                  {t('related.ishii.points.title')}
                </h2>
                {String(t('related.ishii.points.subtitleEn')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="section-title-en text-accent-500 text-[15px] tracking-[0.12em] mb-4">{__s}</p>))}
                <div className="gold-line mx-auto"></div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {points.map((n) => (
                  <div key={n} className="bg-background-50 border border-background-200/60 p-7">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full border border-accent-500/40 text-accent-600 mb-5 font-heading text-[15px]">
                      {n}
                    </div>
                    <h3 className="font-heading text-[16px] text-foreground-900 tracking-[0.05em] mb-3">
                      {t(`related.ishii.points.${n}.title`)}
                    </h3>
                    {renderParagraphs(
                      t(`related.ishii.points.${n}.desc`),
                      'text-[14px] text-foreground-500 tracking-[0.03em] leading-relaxed'
                    )}
                  </div>
                ))}
              </div>
            </div>
          </section>
        </FadeInSection>

        {/* Gallery */}
        <FadeInSection>
          <section className="relative py-16 md:py-20 bg-background-50">
            <div className="max-w-[1100px] mx-auto px-8">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {gallery.map((img, idx) => (
                  <div key={idx} className="relative h-[220px] overflow-hidden">
                    <img src={img.src} alt={img.alt} className="absolute inset-0 w-full h-full object-cover" />
                  </div>
                ))}
              </div>
            </div>
          </section>
        </FadeInSection>

        {/* Relationship with TJBA */}
        <FadeInSection>
          <section className="relative py-20 md:py-28 bg-primary-900 text-foreground-200">
            <div className="max-w-[820px] mx-auto px-8 text-center">
              {String(t('related.ishii.role.label')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="text-accent-400 text-[13px] tracking-[0.2em] mb-5 font-label">{__s}</p>))}
              <h2 className="font-heading text-xl md:text-2xl text-foreground-50 tracking-[0.06em] mb-2">
                {t('related.ishii.role.title')}
              </h2>
              {String(t('related.ishii.role.subtitleEn')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="section-title-en text-accent-400 text-[15px] tracking-[0.12em] mb-6">{__s}</p>))}
              <div className="gold-line mx-auto mb-8"></div>
              <div className="space-y-6 text-[15px] md:text-[16px] text-foreground-200/80 tracking-[0.03em] leading-loose text-left">
                <p>
                  {t('related.ishii.role.para1')}{' '}
                  <a
                    href={ISHII_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-accent-400 hover:text-accent-300 underline transition-colors"
                  >
                    {t('related.ishii.role.para1Link')}
                  </a>
                </p>
                <p>
                  {t('related.ishii.role.para2')}{' '}
                  <a
                    href={ISHII_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-accent-400 hover:text-accent-300 underline transition-colors"
                  >
                    {t('related.ishii.role.para2Link')}
                  </a>
                </p>
              </div>
            </div>
          </section>
        </FadeInSection>

        {/* Official site */}
        <FadeInSection>
          <section className="relative py-20 md:py-24 bg-background-50">
            <div className="max-w-[720px] mx-auto px-8 text-center">
              <h2 className="font-heading text-xl md:text-2xl text-foreground-900 tracking-[0.06em] mb-4">
                {t('related.ishii.official.title')}
              </h2>
              {renderParagraphs(
                t('related.ishii.official.body'),
                'text-[15px] text-foreground-600 tracking-[0.03em] leading-relaxed mb-8'
              )}
              <a
                href={ISHII_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-accent-500 hover:bg-accent-400 text-primary-900 text-[15px] tracking-[0.05em] px-8 py-3 transition-colors"
              >
                <span>{t('related.common.official')}</span>
                <i className="ri-external-link-line text-sm"></i>
              </a>
              <p className="mt-4 text-[13px] text-foreground-400 tracking-[0.04em]">
                {t('related.ishii.official.url')} — {t('related.common.externalNote')}
              </p>
            </div>
          </section>
        </FadeInSection>

        {/* Other related */}
        <FadeInSection>
          <section className="relative py-16 md:py-20 bg-background-100/50">
            <div className="max-w-[900px] mx-auto px-8">
              <div className="text-center mb-10">
                <h2 className="font-heading text-lg md:text-xl text-foreground-900 tracking-[0.06em] mb-2">
                  {t('related.common.otherTitle')}
                </h2>
                <div className="gold-line mx-auto"></div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Link
                  to={href('/related/family-romance')}
                  className="card-glow group block bg-background-50 border border-background-200/60 p-5"
                >
                  <h3 className="font-heading text-[16px] text-foreground-900 tracking-[0.05em] mb-2 group-hover:text-accent-600 transition-colors">
                    {t('related.hub.card.fr.name')}
                  </h3>
                  {String(t('related.hub.card.fr.role')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="text-[14px] text-foreground-500 tracking-[0.03em] leading-relaxed">{__s}</p>))}
                </Link>
                <Link
                  to={href('/related/butler-bell')}
                  className="card-glow group block bg-background-50 border border-background-200/60 p-5"
                >
                  <h3 className="font-heading text-[16px] text-foreground-900 tracking-[0.05em] mb-2 group-hover:text-accent-600 transition-colors">
                    {t('related.hub.card.bell.name')}
                  </h3>
                  {String(t('related.hub.card.bell.role')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="text-[14px] text-foreground-500 tracking-[0.03em] leading-relaxed">{__s}</p>))}
                </Link>
              </div>
              <div className="text-center pt-8">
                <Link
                  to={href('/related')}
                  className="text-[15px] text-accent-600 hover:text-accent-500 tracking-[0.05em] transition-colors"
                >
                  {t('related.common.backToHub')}
                </Link>
              </div>
            </div>
          </section>
        </FadeInSection>

        {/* Last updated */}
        <div className="bg-background-100 py-8">
          {String(t('related.ishii.lastUpdated')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="text-center text-[13px] text-foreground-400 tracking-[0.05em]">{__s}</p>))}
        </div>

        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="fixed bottom-8 right-8 w-11 h-11 rounded-full border border-accent-500/40 text-accent-500 hover:bg-accent-500/10 hover:border-accent-400 transition-all duration-300 flex items-center justify-center cursor-pointer z-40"
          aria-label={t('related.common.backToTop')}
        >
          <i className="ri-arrow-up-line"></i>
        </button>
      </main>
      <Footer />
    </>
  );
}
